/**
 * mockWarehouseStore.ts
 * In-memory CRUD store for Warehouse (Gudang) master data.
 * Each inventory cost-layer row can reference multiple warehouse location entries.
 */

import { inventoryCostLayerDatabase } from './mockGrStore';
import { syncWarehouseToDatabase } from './dbBridge';

export interface WarehouseRecord {
  id: string;
  code: string;
  name: string;
  type: 'MAIN' | 'SUPPORT' | 'QUARANTINE' | 'FINISHED_GOODS' | 'RAW_MATERIALS';
  address?: string;
  picName?: string;   // Person in Charge
  picPhone?: string;
  capacity?: number;  // in pallets or m²
  capacityUnit?: string;
  status: 'ACTIVE' | 'INACTIVE';
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryLocationEntry {
  inventoryId: string;
  itemCode: string;
  itemName: string;
  condition: string;
  status: string;
  remainingQty: number;
  uomCode: string;
  unitCost: number;
  batchNumber: string;
  supplierName: string;
  grNumber: string;
  createdAt: string;
}

export interface WarehouseDetailData {
  warehouse: WarehouseRecord;
  inventoryEntries: InventoryLocationEntry[];
  kpis: {
    totalSkuCount: number;
    totalStockUnits: number;
    totalInventoryValue: number;
    availableCount: number;
    quarantineCount: number;
  };
}

// ─── SEED DATA ───────────────────────────────────────────────────────────────

export const warehouseDatabase: WarehouseRecord[] = [
  {
    id: 'wh-001',
    code: 'GU-MAIN',
    name: 'Gudang Utama',
    type: 'MAIN',
    address: 'Area Produksi Blok A, Pabrik PT XYZ',
    picName: 'Agus Wibowo',
    picPhone: '+62-812-3456-7890',
    capacity: 200,
    capacityUnit: 'pallet',
    status: 'ACTIVE',
    notes: 'Gudang penyimpanan utama untuk bahan baku dan packaging siap pakai.',
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-09-01T00:00:00.000Z',
  },
  {
    id: 'wh-002',
    code: 'GU-PENUNJANG',
    name: 'Gudang Penunjang',
    type: 'SUPPORT',
    address: 'Area Produksi Blok B, Pabrik PT XYZ',
    picName: 'Budi Santoso',
    picPhone: '+62-813-9876-5432',
    capacity: 100,
    capacityUnit: 'pallet',
    status: 'ACTIVE',
    notes: 'Gudang pendukung untuk consumables, spare parts, dan alat bantu produksi.',
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-09-01T00:00:00.000Z',
  },
  {
    id: 'wh-003',
    code: 'GU-KARANTINA',
    name: 'Gudang Karantina & Khusus',
    type: 'QUARANTINE',
    address: 'Area QC Blok D, Pabrik PT XYZ',
    picName: 'Dewi Lestari',
    picPhone: '+62-811-5678-9012',
    capacity: 50,
    capacityUnit: 'pallet',
    status: 'ACTIVE',
    notes: 'Area penyimpanan barang defect, karantina QC, dan barang dengan kondisi khusus.',
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-09-01T00:00:00.000Z',
  },
];

// Map from location keyword → warehouse id (for cross-referencing cost layer warehouseLocation)
const LOCATION_KEYWORD_MAP: Record<string, string> = {
  'gudang utama': 'wh-001',
  'gudang penunjang': 'wh-002',
  'gudang karantina': 'wh-003',
};

function resolveWarehouseId(warehouseLocation: string): string | null {
  const lower = warehouseLocation.toLowerCase();
  for (const [keyword, id] of Object.entries(LOCATION_KEYWORD_MAP)) {
    if (lower.includes(keyword)) return id;
  }
  return null;
}

// ─── CRUD ────────────────────────────────────────────────────────────────────

export function getAllWarehouses(search?: string, type?: string): WarehouseRecord[] {
  let list = [...warehouseDatabase];
  if (search) {
    const q = search.toLowerCase();
    list = list.filter(
      (w) =>
        w.code.toLowerCase().includes(q) ||
        w.name.toLowerCase().includes(q) ||
        (w.address || '').toLowerCase().includes(q) ||
        (w.picName || '').toLowerCase().includes(q)
    );
  }
  if (type && type !== 'ALL') {
    list = list.filter((w) => w.type === type);
  }
  return list;
}

export function getWarehouseById(id: string): WarehouseRecord | undefined {
  return warehouseDatabase.find(
    (w) => w.id === id || w.code.toLowerCase() === id.toLowerCase()
  );
}

export function createWarehouse(data: Partial<WarehouseRecord>): WarehouseRecord {
  if (!data.code || !data.name || !data.type) {
    throw new Error('Kode, nama, dan tipe gudang wajib diisi.');
  }
  const dup = warehouseDatabase.find(
    (w) => w.code.toLowerCase() === data.code!.toLowerCase()
  );
  if (dup) throw new Error(`Gudang dengan kode '${data.code}' sudah terdaftar.`);

  const newRecord: WarehouseRecord = {
    id: `wh-${Date.now()}`,
    code: data.code.toUpperCase(),
    name: data.name,
    type: data.type,
    address: data.address,
    picName: data.picName,
    picPhone: data.picPhone,
    capacity: data.capacity,
    capacityUnit: data.capacityUnit || 'pallet',
    status: data.status || 'ACTIVE',
    notes: data.notes,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  warehouseDatabase.push(newRecord);
  syncWarehouseToDatabase(newRecord);
  return newRecord;
}

export function updateWarehouse(id: string, data: Partial<WarehouseRecord>): WarehouseRecord {
  const idx = warehouseDatabase.findIndex(
    (w) => w.id === id || w.code.toLowerCase() === id.toLowerCase()
  );
  if (idx === -1) throw new Error(`Gudang dengan ID '${id}' tidak ditemukan.`);

  if (data.code && data.code !== warehouseDatabase[idx].code) {
    const dup = warehouseDatabase.find(
      (w, j) => j !== idx && w.code.toLowerCase() === data.code!.toLowerCase()
    );
    if (dup) throw new Error(`Kode gudang '${data.code}' sudah digunakan.`);
  }

  warehouseDatabase[idx] = {
    ...warehouseDatabase[idx],
    ...data,
    id: warehouseDatabase[idx].id,
    updatedAt: new Date().toISOString(),
  };
  syncWarehouseToDatabase(warehouseDatabase[idx]);
  return warehouseDatabase[idx];
}

export function deleteWarehouse(id: string): void {
  const idx = warehouseDatabase.findIndex(
    (w) => w.id === id || w.code.toLowerCase() === id.toLowerCase()
  );
  if (idx === -1) throw new Error(`Gudang dengan ID '${id}' tidak ditemukan.`);

  // Guard: check if any active inventory uses this warehouse
  const wh = warehouseDatabase[idx];
  const keywordMatch = Object.entries(LOCATION_KEYWORD_MAP).find(
    ([, whId]) => whId === wh.id
  );
  if (keywordMatch) {
    const hasStock = inventoryCostLayerDatabase.some(
      (l) =>
        l.warehouseLocation.toLowerCase().includes(keywordMatch[0]) &&
        l.remainingQty > 0
    );
    if (hasStock) {
      throw new Error(
        `Gudang '${wh.name}' masih memiliki stok inventaris aktif. Pindahkan stok terlebih dahulu sebelum menghapus gudang.`
      );
    }
  }

  warehouseDatabase.splice(idx, 1);
}

// ─── DETAIL WORKSPACE ────────────────────────────────────────────────────────

export function getWarehouseDetailData(id: string): WarehouseDetailData {
  const warehouse = getWarehouseById(id);
  if (!warehouse) throw new Error(`Gudang dengan ID '${id}' tidak ditemukan.`);

  // Find cost layer entries that belong to this warehouse
  const entries: InventoryLocationEntry[] = inventoryCostLayerDatabase
    .filter((l) => {
      const whId = resolveWarehouseId(l.warehouseLocation);
      return whId === warehouse.id;
    })
    .map((l) => ({
      inventoryId: l.inventoryId,
      itemCode: l.itemCode,
      itemName: l.itemName,
      condition: l.condition,
      status: l.status,
      remainingQty: l.remainingQty,
      uomCode: l.uomCode,
      unitCost: l.unitCost,
      batchNumber: l.batchNumber,
      supplierName: l.supplierName || '',
      grNumber: l.grNumber,
      createdAt: l.createdAt,
    }));

  const totalStockUnits = entries.reduce((s, e) => s + e.remainingQty, 0);
  const totalInventoryValue = entries.reduce(
    (s, e) => s + e.remainingQty * e.unitCost,
    0
  );
  const availableCount = entries.filter((e) => e.status === 'AVAILABLE').length;
  const quarantineCount = entries.filter((e) => e.status === 'QUARANTINE').length;
  const uniqueItems = new Set(entries.map((e) => e.itemCode));

  return {
    warehouse,
    inventoryEntries: entries,
    kpis: {
      totalSkuCount: uniqueItems.size,
      totalStockUnits,
      totalInventoryValue,
      availableCount,
      quarantineCount,
    },
  };
}

/**
 * Get all warehouses that hold stock for a given inventoryId (cost layer).
 * Since each cost layer has a single warehouseLocation string (which may reference
 * a single warehouse), this returns the resolved warehouse for that entry.
 */
export function getWarehousesForInventoryId(inventoryId: string): WarehouseRecord[] {
  const layer = inventoryCostLayerDatabase.find(
    (l) => l.inventoryId === inventoryId
  );
  if (!layer) return [];
  const whId = resolveWarehouseId(layer.warehouseLocation);
  if (!whId) return [];
  const wh = getWarehouseById(whId);
  return wh ? [wh] : [];
}

/**
 * Get warehouse summary for every cost-layer item:
 * returns a map of inventoryId → { warehouse, rackLocation }
 */
export function getInventoryWarehouseMap(): Map<
  string,
  { warehouse: WarehouseRecord; rackLocation: string }[]
> {
  const result = new Map<
    string,
    { warehouse: WarehouseRecord; rackLocation: string }[]
  >();

  for (const layer of inventoryCostLayerDatabase) {
    const whId = resolveWarehouseId(layer.warehouseLocation);
    const wh = whId ? getWarehouseById(whId) : undefined;
    if (wh) {
      // Extract rack location (text after the dash, e.g. "Rak Pallet B-01")
      const parts = layer.warehouseLocation.split(' - ');
      const rackLocation = parts.length > 1 ? parts.slice(1).join(' - ') : layer.warehouseLocation;
      const existing = result.get(layer.inventoryId) || [];
      existing.push({ warehouse: wh, rackLocation });
      result.set(layer.inventoryId, existing);
    }
  }
  return result;
}
